import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { PROJECTS } from "../data/content";
import { PAGE_SEO } from "../data/seoContent";
import SEO, { breadcrumbSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

export default function Projects() {
  const crumbs = [{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }];
  return (
    <div data-testid="page-projects">
      <SEO {...PAGE_SEO.projects} schema={breadcrumbSchema(crumbs)} />
      <section className="bg-[#0A0F1C] text-white pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Case Studies</p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            Programmes, not projects.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
            Compliance programmes delivered for landmark UK clients — scoped to sector risk, resourced nationally and documented to standards that stand up to regulator and tender scrutiny.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto">
          {PROJECTS.map((p, i) => (
            <article key={i} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-b border-slate-200" data-testid={`project-${i}`}>
              <div className={`aspect-[4/3] lg:aspect-auto overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-10 lg:p-16 flex flex-col justify-center">
                <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">{p.sector}</p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-4">{p.title}</h2>
                <p className="text-sm text-[#1FA59A] font-semibold mb-3">{p.client}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-6">{p.discipline}</p>

                <p className="text-slate-700 text-lg leading-relaxed mb-6">{p.summary}</p>

                <div className="border-t border-slate-200 pt-6 space-y-5">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Scope</p>
                    <p className="text-slate-700 leading-relaxed">{p.scope}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Services delivered</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {p.services.map((s, si) => (
                        <li key={si} className="flex items-start gap-2 text-slate-700 text-sm">
                          <Check className="w-4 h-4 text-[#3FBAB0] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Outcome</p>
                    <p className="text-slate-700 leading-relaxed">{p.outcome}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#0A0F1C] text-white py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-tight">Have a programme in mind?</h2>
          <div className="lg:justify-self-end">
            <Link to="/contact" className="gs-gradient text-white font-bold px-8 py-4 inline-flex items-center gap-2 hover:opacity-90" data-testid="projects-cta">
              Request a proposal <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
